import React from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
  Table,
  UncontrolledTooltip,
} from 'reactstrap';
import {
  IconCalendarEvent,
  IconMail,
  IconPhone,
  IconEye,
  IconRosetteDiscountCheckFilled,
  IconAlertTriangleFilled,
  IconInfoCircle,
} from '@tabler/icons-react';
import { AdvisoryLead } from '../models/dashboardModels';
import {
  ADVISORY_BUSINESS_LINE_NAMES,
  ADVISORY_CONTACT_SCHEDULE_NAMES,
  ADVISORY_DELIVERY_TIME_NAMES,
  ADVISORY_GOAL_NAMES,
  ADVISORY_MONTHLY_BUDGET_NAMES,
  ADVISORY_PROPERTY_TYPE_NAMES,
  getAdvisoryStatusBadgeColor,
  getAdvisoryStatusLabel,
} from '../adapters/advisoryAdapters';

export interface Props {
  leads: AdvisoryLead[];
  footer?: React.ReactNode;
  onViewDetail: (lead: AdvisoryLead) => void;
  headerAction?: React.ReactNode;
}

const DashboardAdvisoryListTable = ({
  leads,
  footer,
  onViewDetail,
  headerAction,
}: Props) => {
  return (
    <Card>
      <CardHeader className="d-flex justify-content-between align-items-center">
        <CardTitle className="mb-0">Agnósticas</CardTitle>
        {headerAction}
      </CardHeader>
      <CardBody>
        <div className="table-responsive">
          <Table striped hover>
            <thead>
              <tr>
                <th>Contacto</th>
                <th>Línea</th>
                <th>Interés</th>
                <th>Asignado</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No hay asesorías registradas.
                  </td>
                </tr>
              )}
              {leads.map((lead) => (
                <tr key={lead.id}>
                  <td>
                    <div>
                      <div className="d-flex align-items-center">
                        <span className="font-weight-bold">
                          {lead.customerProfile.first_name}{' '}
                          {lead.customerProfile.last_name}
                        </span>
                        <span
                          id={`advisory-verified-${lead.id}`}
                          className="ms-1 d-inline-flex"
                        >
                          {lead.is_customer_verified ? (
                            <IconRosetteDiscountCheckFilled
                              size={16}
                              className="text-success"
                            />
                          ) : (
                            <IconAlertTriangleFilled
                              size={16}
                              className="text-warning"
                            />
                          )}
                        </span>
                        <UncontrolledTooltip
                          delay={0}
                          target={`advisory-verified-${lead.id}`}
                        >
                          {lead.is_customer_verified
                            ? 'El usuario ya verificó su cuenta desde la plataforma'
                            : 'El usuario no ha verificado su cuenta'}
                        </UncontrolledTooltip>
                      </div>
                      <div className="d-flex align-items-center text-muted">
                        <IconMail size={12} className="me-1" />
                        {lead.customerProfile.email}
                      </div>
                      <div className="d-flex align-items-center text-muted">
                        <IconPhone size={12} className="me-1" />
                        {lead.customerProfile.area_code || '+503'}{' '}
                        {lead.customerProfile.phone}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="font-weight-bold">
                      {ADVISORY_BUSINESS_LINE_NAMES[lead.delivery_time] ??
                        lead.delivery_time}
                    </span>{' '}
                    (
                    {ADVISORY_DELIVERY_TIME_NAMES[lead.delivery_time] ??
                      lead.delivery_time}
                    )
                  </td>
                  <td>
                    <div>
                      <div>{ADVISORY_GOAL_NAMES[lead.goal] ?? lead.goal}</div>
                      <div>
                        {ADVISORY_PROPERTY_TYPE_NAMES[lead.property_type] ??
                          lead.property_type}
                      </div>
                      <div className="text-muted">{lead.zone || "Sin zona especificada"}</div>
                      <div className="text-muted">
                        {ADVISORY_MONTHLY_BUDGET_NAMES[lead.monthly_budget] ??
                          lead.monthly_budget}
                      </div>
                      <div className="text-muted small">
                        Horario:{' '}
                        {ADVISORY_CONTACT_SCHEDULE_NAMES[
                          lead.contact_schedule
                        ] ?? lead.contact_schedule}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      {lead.assignedSdr && (
                        <div className="font-weight-bold">
                          (SDR) {lead.assignedSdr.first_name}{' '}
                          {lead.assignedSdr.last_name}
                        </div>
                      )}
                      {lead.assignedProper && (
                        <div className="font-weight-bold">
                          (Proper) {lead.assignedProper.first_name}{' '}
                          {lead.assignedProper.last_name}
                        </div>
                      )}
                      {lead.scheduled_at && (
                        <Badge
                          color="info"
                          className="d-inline-flex align-items-center mt-2 px-2 py-1"
                          style={{ fontSize: '0.85rem' }}
                        >
                          <IconCalendarEvent size={14} className="me-1" />
                          {new Date(lead.scheduled_at).toLocaleString()}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td>
                    <Badge
                      className="badge-lg"
                      color={getAdvisoryStatusBadgeColor(lead.status)}
                    >
                      {getAdvisoryStatusLabel(lead.status)}
                    </Badge>
                    {lead.cancellation_reason && (
                      <>
                        <span
                          id={`advisory-cancellation-reason-${lead.id}`}
                          className="ms-1 d-inline-flex"
                        >
                          <IconInfoCircle size={16} className="text-muted" />
                        </span>
                        <UncontrolledTooltip
                          delay={0}
                          target={`advisory-cancellation-reason-${lead.id}`}
                        >
                          {lead.cancellation_reason}
                        </UncontrolledTooltip>
                      </>
                    )}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      color="outline-secondary"
                      onClick={() => onViewDetail(lead)}
                    >
                      <IconEye size={16} className="me-1" />
                      Ver detalles
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </CardBody>
      <CardFooter>{footer && <div>{footer}</div>}</CardFooter>
    </Card>
  );
};

export default DashboardAdvisoryListTable;
